import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useShipmentsExportReport } from '../../hooks/useShipmentsExportReport';
import { formatAge } from '../../utils/formatAge';

interface Facility {
  id: number;
  name: string;
}

interface ShipmentsExportModalProps {
  open: boolean;
  onClose: () => void;
  facilities: Facility[];
  userId: string;
  jwtToken: string;
  websocketUrl: string;
}

const FALLBACK_rangeDaysLimit = 90;

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ShipmentsExportModal: React.FC<ShipmentsExportModalProps> = ({
  open,
  onClose,
  facilities,
  userId,
  jwtToken,
  websocketUrl,
}) => {
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const {
    isGenerating,
    cacheStatus,
    error,
    maxRangeDays,
    checkCache,
    generateReport,
    resetState,
  } = useShipmentsExportReport({ userId, jwtToken, websocketUrl, onDownload: onClose });

  const rangeDaysLimit = maxRangeDays || FALLBACK_rangeDaysLimit;

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedFacilityId('');
      setStartDate(null);
      setEndDate(null);
      resetState();
    }
  }, [open, resetState]);

  // Check cache when facility or dates change
  useEffect(() => {
    if (open && selectedFacilityId !== '' && startDate && endDate) {
      checkCache(selectedFacilityId, formatDate(startDate), formatDate(endDate));
    }
  }, [open, selectedFacilityId, startDate, endDate, checkCache]);

  const getMinStartDate = useCallback((): Date => {
    // No hard minimum, but if endDate is set, start can't be more than 90 days before end
    if (endDate) {
      const min = new Date(endDate);
      min.setDate(min.getDate() - rangeDaysLimit);
      return min;
    }
    return new Date(2020, 0, 1);
  }, [endDate, rangeDaysLimit]);

  const getMaxEndDate = useCallback((): Date => {
    const futureMax = new Date();
    futureMax.setDate(futureMax.getDate() + rangeDaysLimit);
    if (startDate) {
      const maxEnd = new Date(startDate);
      maxEnd.setDate(maxEnd.getDate() + rangeDaysLimit);
      return maxEnd < futureMax ? maxEnd : futureMax;
    }
    return futureMax;
  }, [startDate, rangeDaysLimit]);

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    // If end date is now out of range, reset it
    if (date && endDate) {
      const maxEnd = new Date(date);
      maxEnd.setDate(maxEnd.getDate() + rangeDaysLimit);
      if (endDate > maxEnd || endDate < date) {
        setEndDate(null);
      }
    }
  };

  const handleDownload = () => {
    if (selectedFacilityId === '' || !startDate || !endDate) return;
    generateReport(selectedFacilityId, formatDate(startDate), formatDate(endDate), false);
  };

  const handleForceNew = () => {
    if (selectedFacilityId === '' || !startDate || !endDate) return;
    generateReport(selectedFacilityId, formatDate(startDate), formatDate(endDate), true);
  };

  const handleClose = () => {
    if (!isGenerating) {
      onClose();
    }
  };

  const isFormComplete = selectedFacilityId !== '' && startDate !== null && endDate !== null;


  const renderCacheStatus = () => {
    if (!isFormComplete) return null;

    switch (cacheStatus.status) {
      case 'checking':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Checking for cached report...</Typography>
          </Box>
        );
      case 'cached':
        return (
          <Box sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
              Cached report available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generated {formatAge(cacheStatus.age_seconds, cacheStatus.cached_at)}
              {cacheStatus.row_count !== undefined && ` (${cacheStatus.row_count} rows)`}
            </Typography>
          </Box>
        );
      case 'processing':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="info.main">A report is currently being generated...</Typography>
          </Box>
        );
      case 'none':
        return null;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <style>{`.shipments-export-datepicker-popper { z-index: 1500 !important; }`}</style>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Shipments Export
        <IconButton onClick={handleClose} disabled={isGenerating} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="shipments-export-facility-label">Facility</InputLabel>
          <Select
            labelId="shipments-export-facility-label"
            value={selectedFacilityId}
            label="Facility"
            onChange={(e) => setSelectedFacilityId(e.target.value as number)}
            disabled={isGenerating}
          >
            {facilities.map((facility) => (
              <MenuItem key={facility.id} value={facility.id}>
                ({facility.id}) {facility.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
          Appointment date range (max {rangeDaysLimit} days)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              minDate={getMinStartDate()}
              maxDate={endDate || getMaxEndDate()}
              placeholderText="Start date"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              disabled={isGenerating}
              dateFormat="yyyy-MM-dd"
              portalId="root"
              popperClassName="shipments-export-datepicker-popper"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">to</Typography>
          <Box sx={{ flex: 1 }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate || undefined}
              maxDate={getMaxEndDate()}
              placeholderText="End date"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              disabled={isGenerating || !startDate}
              dateFormat="yyyy-MM-dd"
              portalId="root"
              popperClassName="shipments-export-datepicker-popper"
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          {isGenerating ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Generating report...</Typography>
            </Box>
          ) : (
            <>
              {renderCacheStatus()}
              {cacheStatus.status === 'cached' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Force a new report to query the database for the latest information. The cached report was generated {formatAge(cacheStatus.age_seconds, cacheStatus.cached_at)}.
                </Alert>
              )}
            </>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {cacheStatus.status === 'cached' && !isGenerating && (
          <Button
            variant="outlined"
            onClick={handleForceNew}
            disabled={!isFormComplete || isGenerating}
          >
            Force New Report
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={!isFormComplete || isGenerating}
        >
          Download Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShipmentsExportModal;
